import React, { useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { Translate, withLocalize } from 'react-localize-redux';
import PropTypes from 'prop-types';
import { Virtuoso } from 'react-virtuoso';
import css from './AdvancedAutoCompleteDropdown.css';
import Button from '../../atoms/Button';
import { AutoCompleteOptionType } from '../autocompleteTypes';
import { CATEGORY_ALL, CLEAR_TEXT, NO_SEARCH_RESULTS, SELECT_ALL, SELECTED } from './consts';
import OverflowingText from '../../foundations/OverflowingText';

const AutoCompleteCheckbox = React.memo(
    ({ disabled = false, checked = false, indeterminate = false, onChange = () => {} }) => {
        const ref = useRef(null);
        useEffect(() => {
            if (ref.current) ref.current.indeterminate = indeterminate;
        }, [indeterminate]);
        return (
            <input
                ref={ref}
                type="checkbox"
                disabled={disabled}
                onChange={onChange}
                checked={checked}
                className={css.checkbox}
            />
        );
    }
);

AutoCompleteCheckbox.propTypes = {
    disabled: PropTypes.bool,
    checked: PropTypes.bool,
    indeterminate: PropTypes.bool,
    onChange: PropTypes.func,
};

const ROW_SIZE = 36;
const DROPDOWN_HEIGHT = 256;

const AdvancedAutoCompleteDropdown = ({
    groupBy,
    filteredOptions,
    values,
    disabled,
    onChange,
    lazy,
    isMulti,
    renderOption,
    openUpward = false,
    selectableGroupHeaders = false,
    translate,
}) => {
    const groupedOptions = useMemo(() => {
        const groups = groupBy ? groupBy(filteredOptions) : { [CATEGORY_ALL]: filteredOptions };
        return Object.entries(groups).map(([name, options]) => ({
            category: name,
            options,
        }));
    }, [filteredOptions, groupBy]);

    const virtuosoRef = useRef(null);

    useEffect(() => {
        virtuosoRef.current?.scrollToIndex?.(0);
    }, [filteredOptions]);

    const rows = useMemo(() => {
        const listItems = [];
        groupedOptions
            .filter(({ options }) => !!options.length)
            .forEach(({ category, options }) => {
                if (category !== CATEGORY_ALL)
                    listItems.push({ type: 'header', key: `h-${category}`, category, groupOptions: options });
                options.forEach(option => listItems.push({ type: 'option', key: `o-${option.value}`, option }));
            });
        return listItems;
    }, [groupedOptions]);

    const isSelected = option => values.some(val => val.value === option.value);

    const handleGroupHeaderClick = groupOptions => {
        const { isAllSelected, unselectedOptions } = groupOptions.reduce(
            (acc, opt) => {
                if (!isSelected(opt)) {
                    acc.isAllSelected = false;
                    acc.unselectedOptions.push(opt);
                }
                return acc;
            },
            { isAllSelected: true, unselectedOptions: [] }
        );
        const newSelected = isAllSelected
            ? values.filter(val => !groupOptions.some(opt => opt.value === val.value))
            : [...values, ...unselectedOptions];
        onChange(newSelected);
    };

    const handleSelectAllFiltered = () => {
        const isAllSelected = filteredOptions.every(option => isSelected(option));
        // if all selected, remove all selected, else add all options to selected values
        const newSelected = isAllSelected
            ? values.filter(val => !filteredOptions.some(opt => opt.value === val.value))
            : [...new Set([...values, ...filteredOptions.filter(opt => !isSelected(opt))])];
        onChange(newSelected);
    };

    const handleOptionSelected = (option, isOptionSelected) => {
        if (isMulti)
            onChange(isOptionSelected ? values.filter(val => val.value !== option.value) : [...values, option]);
        else if (!isOptionSelected) onChange(option);
    };

    return (
        <div className={classNames(css.dropdown, { [css.openUpward]: openUpward })} role="listbox">
            {isMulti && (
                <div className={css.controls}>
                    <div
                        className={classNames(css.selectAllWrapper, {
                            [css.lazy]: lazy,
                            [css.disabled]: disabled,
                        })}
                        onClick={handleSelectAllFiltered}
                    >
                        {!lazy && (
                            <AutoCompleteCheckbox
                                disabled={disabled || !filteredOptions.length}
                                checked={filteredOptions.length && filteredOptions.every(isSelected)}
                            />
                        )}
                        <Translate id={lazy ? SELECTED : SELECT_ALL} />
                        <span className={css.controlCount}>
                            ({values.filter(val => filteredOptions.some(opt => opt.value === val.value)).length}/
                            {filteredOptions.length})
                        </span>
                    </div>
                    <Button onClick={() => onChange([])} type="link" disabled={disabled} className={css.clear}>
                        <Translate id={CLEAR_TEXT} />
                    </Button>
                </div>
            )}
            <div className={css.list}>
                {!filteredOptions.length ? (
                    <div className={css.noResults}>
                        <Translate id={NO_SEARCH_RESULTS} />
                    </div>
                ) : (
                    <Virtuoso
                        ref={virtuosoRef}
                        style={{ height: Math.min(DROPDOWN_HEIGHT, rows.length * ROW_SIZE), width: '100%' }}
                        data={rows}
                        totalCount={rows.length}
                        computeItemKey={(index, item) => item.key}
                        itemContent={index => {
                            const row = rows[index];
                            if (row.type === 'header') {
                                if (selectableGroupHeaders && isMulti) {
                                    const selectedCount = row.groupOptions.filter(opt => isSelected(opt)).length;
                                    const isAllGroupSelected = selectedCount === row.groupOptions.length;
                                    const isSomeGroupSelected = selectedCount > 0;
                                    return (
                                        <div
                                            className={classNames(css.groupHeader, css.selectableGroupHeader)}
                                            onClick={() => handleGroupHeaderClick(row.groupOptions)}
                                        >
                                            <AutoCompleteCheckbox
                                                disabled={disabled}
                                                checked={isAllGroupSelected}
                                                indeterminate={isSomeGroupSelected && !isAllGroupSelected}
                                                onChange={e => {
                                                    e.stopPropagation();
                                                    handleGroupHeaderClick(row.groupOptions);
                                                }}
                                            />
                                            {row.category}
                                        </div>
                                    );
                                }
                                return (
                                    <div className={css.groupHeader}>
                                        <Translate id={row.category} />
                                    </div>
                                );
                            }
                            const { option } = row;
                            const isOptionSelected = isSelected(option);
                            return (
                                <div
                                    className={classNames(css.item, {
                                        [css.selected]: isOptionSelected,
                                        [css.disabled]: disabled,
                                    })}
                                    style={{ minHeight: ROW_SIZE }}
                                    onClick={() => handleOptionSelected(option, isOptionSelected)}
                                >
                                    {isMulti && (
                                        <AutoCompleteCheckbox
                                            disabled={disabled}
                                            checked={isOptionSelected}
                                            onChange={() => handleOptionSelected(option, isOptionSelected)}
                                        />
                                    )}
                                    {renderOption ? (
                                        renderOption(option)
                                    ) : (
                                        <>
                                            {option.icon}
                                            <OverflowingText>{translate(option.label)}</OverflowingText>
                                        </>
                                    )}
                                </div>
                            );
                        }}
                    />
                )}
            </div>
        </div>
    );
};

AdvancedAutoCompleteDropdown.propTypes = {
    groupBy: PropTypes.func,
    filteredOptions: PropTypes.arrayOf(PropTypes.shape({})),
    values: PropTypes.arrayOf(AutoCompleteOptionType),
    disabled: PropTypes.bool,
    onChange: PropTypes.func,
    lazy: PropTypes.bool,
    isMulti: PropTypes.bool,
    renderOption: PropTypes.func,
    openUpward: PropTypes.bool,
    selectableGroupHeaders: PropTypes.bool,
    translate: PropTypes.func,
};

export default withLocalize(AdvancedAutoCompleteDropdown);
